import React, { useCallback, useEffect, useState } from "react";

import moment, { Moment } from "moment";

import { useTranslation } from "utils";

import { PracticeCrop, WithInjectedName } from "models";

import { Button, DatePicker, Flex, Form, Option, Select, Typography } from "components";

import { AddEditCropsFormValues } from "pages/app/crop-mix-composer/components/AddEditCropsSection";

type MainCropSelectionFormProps = {
  description: string;
  type: "firstCrop" | "secondCrop";
  availableCrops: Array<WithInjectedName<PracticeCrop>>;
  handleSubmit: () => void;
  cropId?: string;
  date?: string;
};
export const MainCropSelectionForm = ({ description, type, cropId, date, availableCrops, handleSubmit }: MainCropSelectionFormProps) => {
  const { t } = useTranslation("cropMixComposer");

  const form = Form.useFormInstance<AddEditCropsFormValues>();

  const [selectedCropId, setSelectedCropId] = useState<string | undefined>(cropId);
  const [suggestedDate, setSuggestedDate] = useState<Moment>(moment());

  const getSuggestedDate = useCallback(
    (type: "firstCrop" | "secondCrop", cropId?: string) => {
      if (!cropId) return moment();

      const crop = availableCrops?.find((crop) => crop.id === cropId);
      if (!crop) return moment();

      const recommendedSeedingDayAndMonth = crop?.sequestrationValues?.[0]?.seedingStart;
      const recommendedHarvestDayAndMonth = crop.sequestrationValues?.[0]?.harvestStart;
      const yearForRecommendation = moment().year();

      if (type === "secondCrop") {
        const firstCropDate = form.getFieldValue(["firstCrop", "date"]);
        const seedingDate = recommendedSeedingDayAndMonth ? moment(`${recommendedSeedingDayAndMonth}/${yearForRecommendation}`, "DD/MM/YYYY") : moment();

        if (!firstCropDate) return seedingDate;
        if (seedingDate.isBefore(firstCropDate)) return seedingDate.clone().add(1, "year");

        return seedingDate;
      }

      if (type === "firstCrop")
        return recommendedHarvestDayAndMonth ? moment(`${recommendedHarvestDayAndMonth}/${yearForRecommendation}`, "DD/MM/YYYY") : moment();

      return moment();
    },
    [availableCrops, form]
  );

  useEffect(() => {
    const cropIdFromForm = form.getFieldValue([type, "cropId"]);

    setSuggestedDate(cropIdFromForm ? getSuggestedDate(type, cropIdFromForm || selectedCropId) : moment());
  }, [selectedCropId, form, getSuggestedDate, type]);

  return (
    <Flex vertical={true} gap={"2rem"}>
      <Typography variant="paragraph-m" className="margin-0">
        {description}
      </Typography>
      <Select
        dataTestId={"crop-mix-composer-main-crop-popover-form-crop-select"}
        label={t("mainCrop")}
        name={[type, "cropId"]}
        initialValue={availableCrops.find((availableCrop) => availableCrop.id === selectedCropId) ? selectedCropId : null}
        value={selectedCropId}
        onChange={(value) => setSelectedCropId(value)}
        rules={[{ required: true, message: t("errors.cropRequired") }]}
        showSearch
      >
        {availableCrops
          ?.filter((crop) => crop.isMainCrop)
          .sort((a, b) => a.name?.toString().localeCompare(b.name?.toString()))
          .map((option, index) => (
            <Option
              data-testid={`crop-mix-composer-main-crop-popover-form-crop-select-option-${index}`}
              key={`crop-mix-composer-main-crop-popover-form-crop-select-option-${option.id}`}
              value={option.id}
            >
              {option.name}
            </Option>
          ))}
      </Select>
      <DatePicker
        key={suggestedDate?.toString()}
        data-field-id="seedingDate"
        dataTestId={"crop-mix-composer-main-crop-popover-form-date-select"}
        initialValue={date ? moment(date) : null}
        defaultPickerValue={form.getFieldValue([type, "date"]) ? form.getFieldValue([type, "date"]) : date ? moment(date) : suggestedDate || moment()}
        format={["DD.MM.YYYY"]}
        label={type === "firstCrop" ? t("mainCropModal.harvestDate") : t("mainCropModal.seedingDate")}
        name={[type, "date"]}
        rules={[{ required: true, message: t(`errors.${type === "firstCrop" ? "harvestDateRequired" : "seedingDateRequired"}`) }]}
        labelClassName={"required"}
      />
      <Flex justify="flex-end">
        <Button
          dataTestId="crop-mix-composer-main-crop-popover-form-submit-button"
          className="crop-explorer save-button"
          type="primary"
          size="small"
          htmlType="submit"
          onClick={handleSubmit}
        >
          {t("save")}
        </Button>
      </Flex>
    </Flex>
  );
};
